import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { api } from '../../api/client';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState({
    name: '',
    nameKana: '',
    department: '',
    phone: '',
  });
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        nameKana: user.nameKana || '',
        department: user.department || '',
        phone: user.phone || '',
      });
      setAvatarPreview(user.avatarUrl || null);
    }
  }, [user]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // フロントエンドでリサイズ
    const resizedBlob = await resizeImage(file, 256);
    const resizedFile = new File([resizedBlob], file.name, { type: 'image/webp' });
    
    setAvatarFile(resizedFile);
    setAvatarPreview(URL.createObjectURL(resizedBlob));
  };

  const resizeImage = async (file: File, maxSize: number = 256): Promise<Blob> => {
    return new Promise((resolve) => {
      const img = new Image();
      const canvas = document.createElement('canvas');
      img.src = URL.createObjectURL(file);
      img.onload = () => {
        let { width, height } = img;
        if (width > height) {
          if (width > maxSize) {
            height = (height * maxSize) / width;
            width = maxSize;
          }
        } else {
          if (height > maxSize) {
            width = (width * maxSize) / height;
            height = maxSize;
          }
        }
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d')!.drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => resolve(blob!), 'image/webp', 0.85);
      };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setMessage(null);

    try {
      // アバターがある場合はアップロード
      let avatarUrl = user?.avatarUrl;
      if (avatarFile) {
        const formDataUpload = new FormData();
        formDataUpload.append('file', avatarFile);
        const uploadResult = await api.upload<{ url: string }>('/api/upload/avatar', formDataUpload);
        avatarUrl = uploadResult.url;
      }

      await api.put('/api/users/profile', { ...formData, avatarUrl });
      refreshUser?.();
      setMessage({ type: 'success', text: 'プロフィールを更新しました' });
    } catch {
      setMessage({ type: 'error', text: 'プロフィールの更新に失敗しました' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white">プロフィール設定</h1>

      <Card>
        <form onSubmit={handleSubmit} className="space-y-6">
          {message && (
            <div
              className={`p-3 rounded-lg text-sm ${
                message.type === 'success'
                  ? 'bg-green-50 text-green-700 border border-green-200'
                  : 'bg-red-50 text-red-700 border border-red-200'
              }`}
            >
              {message.text}
            </div>
          )}

          {/* アバター */}
          <div className="flex items-center gap-6">
            <div
              onClick={handleAvatarClick}
              className="relative w-24 h-24 rounded-full bg-blue-500 flex items-center justify-center cursor-pointer overflow-hidden group"
            >
              {avatarPreview ? (
                <img src={avatarPreview} alt="Avatar" className="w-full h-full object-cover" />
              ) : (
                <span className="text-white text-3xl font-bold">
                  {user?.name?.charAt(0).toUpperCase() || 'U'}
                </span>
              )}
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              </div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleAvatarChange}
              className="hidden"
            />
            <div>
              <h3 className="font-medium text-gray-900 dark:text-white">プロフィール写真</h3>
              <p className="text-sm text-gray-500">クリックして画像を変更</p>
              <p className="text-xs text-gray-400 mt-1">推奨: 256x256px, 5MB以下</p>
            </div>
          </div>

          {/* 基本情報 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="メールアドレス"
              value={user?.email || ''}
              disabled
              className="bg-gray-50"
            />
            <Input
              label="氏名"
              name="name"
              value={formData.name}
              onChange={handleChange}
              maxLength={255}
              required
            />
            <Input
              label="氏名（カナ）"
              name="nameKana"
              value={formData.nameKana}
              onChange={handleChange}
              maxLength={255}
            />
            <Input
              label="部署"
              name="department"
              value={formData.department}
              onChange={handleChange}
              maxLength={255}
            />
            <Input
              label="電話番号"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              maxLength={20}
            />
          </div>

          <div className="flex justify-end pt-4">
            <Button type="submit" loading={saving}>
              保存
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
